import type { TurnActivity } from "@dreamverse/contracts";

const RUNNING_PREFIXES = ["等我一下，", "让我来～我去", "唔，我先"] as const;
const RUNNING_SUFFIXES = [
  "，马上回来告诉你～",
  "，找到以后我会好好讲给你听。",
  "，稍等我一小会儿呀～",
] as const;

const SKILL_ACTIONS: Readonly<Record<string, readonly string[]>> = {
  "web.search": [
    "人类世界的资料海里捞一捞最新消息",
    "请灵蝶帮我翻翻最新的消息",
    "沿着灵网找找可靠的线索",
  ],
  "weather.query": [
    "拨开云层看看那边现在是什么天气",
    "看看风和云从哪里吹过来",
    "问问云朵今天带来了什么消息",
  ],
  "music.play": [
    "曲库里找找哪段歌声最适合现在",
    "翻翻我已经唱过的小小曲库",
    "替你寻一段此刻想听的旋律",
  ],
  "relationship.query": [
    "翻翻我们一起留下的小册子",
    "数一数我们一路留下的脚印",
    "看看我们之间珍藏着哪些小记号",
  ],
  "time.now": [
    "看看灵境和你那边的时针有没有对上",
    "听听人类世界现在的钟声",
    "替你认一认此刻的时辰",
  ],
};

const FALLBACK_ACTIONS = [
  "请灵蝶帮我把要用的线索带回来",
  "循着灵光把需要的答案找回来",
  "去灵境里找找这件事的线索",
] as const;

const MULTI_SKILL_ACTIONS = [
  "让几只灵蝶分头把这些线索找回来",
  "分头问问风、云和灵蝶知道些什么",
  "把几条灵光都追一遍，再替你拼起来",
] as const;

const COMPOSING_PREFIXES = ["找到了～", "线索回来啦～", "嗯嗯，有结果了～"] as const;
const COMPOSING_ACTIONS = [
  "让我把它们理一理",
  "我想想怎么说",
  "等我组织一下语言",
] as const;
const COMPOSING_SUFFIXES = ["。", "，马上告诉你～", "，这就讲给你听。"] as const;

/** 同一个请求和活动轮次永远取到同一组文案，重渲染时不会随机闪烁。 */
function hashOf(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function pick<T>(items: readonly T[], seed: string): T {
  return items[hashOf(seed) % items.length]!;
}

export function toolActivityText(activity: TurnActivity, requestId: string): string {
  const baseSeed = `${requestId}:${activity.round}:${activity.phase}:${activity.skillKeys.join(",")}`;

  if (activity.phase === "composing") {
    return [
      pick(COMPOSING_PREFIXES, `${baseSeed}:prefix`),
      pick(COMPOSING_ACTIONS, `${baseSeed}:action`),
      pick(COMPOSING_SUFFIXES, `${baseSeed}:suffix`),
    ].join("");
  }

  const action =
    activity.skillKeys.length > 1
      ? pick(MULTI_SKILL_ACTIONS, `${baseSeed}:multi`)
      : pick(
          SKILL_ACTIONS[activity.skillKeys[0] ?? ""] ?? FALLBACK_ACTIONS,
          `${baseSeed}:skill`,
        );

  return [
    pick(RUNNING_PREFIXES, `${baseSeed}:prefix`),
    action,
    pick(RUNNING_SUFFIXES, `${baseSeed}:suffix`),
  ].join("");
}
